import { supabase } from "../../lib/supabase";
import {
  calculateNextDailySystemReminderRun,
  calculateNextReminderRun,
  getBrowserTimezone,
} from "./v2ReminderSchedule";
import { loadV2MedicationReminders, readV2Json, writeV2Json } from "./v2Storage";

const REMINDER_ID_MAP_KEY = "ddp-v2-server-reminder-id-map";
const REMINDER_MIGRATION_KEY = "ddp-v2-server-reminder-migration-v1";

export const V2_SERVER_SYNC_STATUS = Object.freeze({
  active: "active",
  syncing: "syncing",
  unavailable: "unavailable",
  incomplete: "incomplete",
});

export async function ensureV2ReminderIdentity(client = supabase) {
  const sessionResult = await client.auth.getSession();
  if (sessionResult.error) {
    return { ok: false, user: null, error: sessionResult.error.message || "Could not read Supabase session." };
  }

  const existingUser = sessionResult.data?.session?.user;
  if (existingUser?.id) {
    return { ok: true, user: existingUser, error: "" };
  }

  const anonymousResult = await client.auth.signInAnonymously();
  if (anonymousResult.error || !anonymousResult.data?.user?.id) {
    return {
      ok: false,
      user: null,
      error:
        anonymousResult.error?.message ||
        "Anonymous sign-in is not enabled. Enable Supabase anonymous sign-ins to activate automatic reminders.",
    };
  }

  return { ok: true, user: anonymousResult.data.user, error: "" };
}

export async function syncV2NotificationSettings(settings = {}, { client = supabase } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const reminders = settings.reminders || settings || {};
  const timezone = getBrowserTimezone();
  const dailySystemReminder = reminders.daily_system_reminder || {};
  const row = {
    user_id: identity.user.id,
    whatsapp_number: String(reminders.whatsapp_number || "").trim(),
    whatsapp_reminders_enabled: Boolean(reminders.whatsapp_reminders_enabled),
    whatsapp_consent: Boolean(reminders.whatsapp_consent),
    whatsapp_include_medication_name: Boolean(reminders.whatsapp_include_medication_name),
    push_notifications_enabled: Boolean(reminders.push_notifications_enabled),
    daily_system_reminder_enabled: Boolean(dailySystemReminder.enabled),
    daily_system_reminder_time: dailySystemReminder.time || null,
    daily_system_reminder_message: String(dailySystemReminder.message || "").trim() || null,
    daily_system_next_run_at: calculateNextDailySystemReminderRun(dailySystemReminder, { timezone }),
    timezone,
    updated_at: new Date().toISOString(),
  };

  const result = await client
    .from("v2_notification_settings")
    .upsert(row, { onConflict: "user_id" })
    .select()
    .single();

  if (result.error) {
    return { ok: false, user: identity.user, error: result.error.message || "Settings sync failed." };
  }

  return { ok: true, user: identity.user, settings: result.data, error: "" };
}

export async function syncV2MedicationReminder(reminder = {}, { client = supabase } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const timezone = getBrowserTimezone();
  const serverId = getServerReminderId(reminder.id);
  const row = {
    user_id: identity.user.id,
    medication_name: String(reminder.medication_name || "").trim(),
    dose: String(reminder.dose || "").trim() || null,
    times_per_day: Number(reminder.times_per_day) || 1,
    reminder_times: Array.isArray(reminder.reminder_times) ? reminder.reminder_times : [reminder.reminder_time],
    frequency: reminder.frequency,
    specific_days: Array.isArray(reminder.specific_days) ? reminder.specific_days : [],
    once_date: reminder.once_date || null,
    active: reminder.active !== false,
    channel: reminder.channel || "app",
    timezone,
    next_run_at:
      reminder.active === false ? null : calculateNextReminderRun(reminder, { timezone, now: new Date() }),
    updated_at: new Date().toISOString(),
  };

  const query = serverId
    ? client.from("v2_medication_reminders").update(row).eq("id", serverId).eq("user_id", identity.user.id)
    : client.from("v2_medication_reminders").insert(row);

  const result = await query.select().single();
  if (result.error) {
    return { ok: false, user: identity.user, error: result.error.message || "Reminder sync failed." };
  }

  rememberServerReminderId(reminder.id, result.data.id);
  return { ok: true, user: identity.user, reminder: result.data, error: "" };
}

export async function setV2ServerReminderActive(localReminderId, active, { client = supabase, isGuest = false } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const serverId = getServerReminderId(localReminderId);
  if (!serverId) return { ok: true, user: identity.user, skipped: true, error: "" };

  const localReminder = loadV2MedicationReminders({ isGuest }).find((item) => item.id === localReminderId);
  const timezone = getBrowserTimezone();
  const nextRunAt =
    active && localReminder
      ? calculateNextReminderRun({ ...localReminder, active: true }, { timezone, now: new Date() })
      : null;

  const result = await client
    .from("v2_medication_reminders")
    .update({
      active: Boolean(active),
      timezone,
      next_run_at: nextRunAt,
      updated_at: new Date().toISOString(),
    })
    .eq("id", serverId)
    .eq("user_id", identity.user.id);

  if (result.error) {
    return { ok: false, user: identity.user, error: result.error.message || "Reminder status sync failed." };
  }

  return { ok: true, user: identity.user, error: "" };
}

export async function deleteV2ServerMedicationReminder(localReminderId, { client = supabase } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const serverId = getServerReminderId(localReminderId);
  if (!serverId) return { ok: true, user: identity.user, skipped: true, error: "" };

  const result = await client
    .from("v2_medication_reminders")
    .delete()
    .eq("id", serverId)
    .eq("user_id", identity.user.id);

  if (result.error) {
    return { ok: false, user: identity.user, error: result.error.message || "Reminder delete sync failed." };
  }

  forgetServerReminderId(localReminderId);
  return { ok: true, user: identity.user, error: "" };
}

export async function migrateLocalV2RemindersToServer({ settings, reminders, isGuest = false, client = supabase } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const migrationState = readV2Json(REMINDER_MIGRATION_KEY, {});
  if (migrationState.user_id === identity.user.id && migrationState.completed_at) {
    return { ok: true, user: identity.user, migrated: false, error: "" };
  }

  const settingsResult = await syncV2NotificationSettings(settings, { client });
  if (!settingsResult.ok) return settingsResult;

  const sourceReminders = Array.isArray(reminders) ? reminders : loadV2MedicationReminders({ isGuest });
  for (const reminder of sourceReminders) {
    const result = await syncV2MedicationReminder(reminder, { client });
    if (!result.ok) return result;
  }

  writeV2Json(REMINDER_MIGRATION_KEY, {
    user_id: identity.user.id,
    completed_at: new Date().toISOString(),
  });

  return { ok: true, user: identity.user, migrated: true, error: "" };
}

export function getServerReminderId(localReminderId) {
  return readV2Json(REMINDER_ID_MAP_KEY, {})[localReminderId] || "";
}

function rememberServerReminderId(localReminderId, serverReminderId) {
  if (!localReminderId || !serverReminderId) return;
  writeV2Json(REMINDER_ID_MAP_KEY, {
    ...readV2Json(REMINDER_ID_MAP_KEY, {}),
    [localReminderId]: serverReminderId,
  });
}

function forgetServerReminderId(localReminderId) {
  const map = readV2Json(REMINDER_ID_MAP_KEY, {});
  delete map[localReminderId];
  writeV2Json(REMINDER_ID_MAP_KEY, map);
}
